import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowIcon, CopyIcon, SourceIcon } from "./Icons";
import LegalText from "./LegalText";
import { getTranslationEvidence } from "../lib/translationEvidence";
import { readReaderUrlState, writeReaderUrlState } from "../lib/readerUrlState";
import { formatTimelineDate, legalTimelineFacts } from "../lib/legalTimeline";
import { displayText, hasDisplayText } from "../lib/displayText";
import { typeLabel } from "../lib/legalTaxonomy";
import { useLocale } from "../lib/LocaleProvider";
import { citationsForSegment } from "../lib/legalCitations";
import { sectionPresentation, stableAlignmentKey, uniqueStableAlignmentMap } from "../lib/legalSections";
import { chooseEnglishTitleLine, resolveDocumentTitle } from "../lib/documentTitle";
import {
  downloadReadingCopyUrl,
  isSafeHref,
  publisherUrl,
  readingCopyUrl,
} from "../lib/sourceLinks";

const SECTION_LABEL_KEYS = {
  applicable_law: "applicableLaw",
  question: "question",
  holding: "holding",
  preamble: "preamble",
  provision: "provision",
  parties: "parties",
  issue: "issue",
  source_attribution: "sourceAttribution",
};

function segmentText(segment) {
  return displayText(segment);
}

function findRelevantSegment(segments, query) {
  const section = query.match(/(?:มาตรา|ม\.)\s*([0-9๐-๙]+(?:\s+\S+)?)/i)?.[1];
  if (section) {
    const target = segments.find((item) =>
      segmentText(item).startsWith(`มาตรา ${section}`),
    );
    if (target) return target;
  }
  const tokens = query
    .split(/\s+/)
    .filter((token) => token.length >= 4)
    .slice(0, 8);
  return (
    segments.find((item) => {
      const text = segmentText(item);
      return tokens.filter((token) => text.includes(token)).length >= 2;
    }) || segments[0]
  );
}

function LegalTimeline({ document: legalDocument }) {
  const { locale, text } = useLocale();
  const events = legalTimelineFacts(legalDocument, locale).filter((event) => event.value);
  if (!events.length) return null;
  return (
    <details className="legal-timeline-disclosure">
      <summary aria-label={text("timeline.aria")}>{text("reader.documentDates")}</summary>
      <section className="legal-timeline" aria-label={text("timeline.aria")}>
        {events.map(({ label, value, kind }) => (
          <div className={`${kind === "effective" ? "effective" : ""}`} key={kind}>
            <span className="timeline-dot" aria-hidden="true" />
            <strong>{label}</strong>
            <time dateTime={value}>{formatTimelineDate(value, locale)}</time>
          </div>
        ))}
      </section>
    </details>
  );
}

function EvidenceRail({
  source,
  sourcePreview,
  sourceBusy,
  onClose,
  onCopyCitation,
  copied,
}) {
  const { text } = useLocale();
  const publisher = publisherUrl(source, source?.canonical_source_url);
  const readingCopy = readingCopyUrl(source, sourcePreview);
  const download = downloadReadingCopyUrl(source, sourcePreview);
  return (
    <aside className="evidence-rail" aria-label={text("source.heading")}>
      <header>
        <h2>{text("source.heading")}</h2>
        <button type="button" className="evidence-close" onClick={onClose}>{text("common.close")}</button>
      </header>
      <section>
        {publisher ? (
          <a href={publisher} target="_blank" rel="noreferrer">{text("source.publisherLink")}</a>
        ) : (
          <p>{text("source.noPublisher")}</p>
        )}
        {readingCopy && (
          <a className="source-preserved-link" href={readingCopy} target="_blank" rel="noreferrer">{text("source.readingCopy")}</a>
        )}
        {download && (
          <a className="source-download-link" href={download} download>{text("source.download")}</a>
        )}
        <div className="evidence-source-actions">
          <button type="button" onClick={onCopyCitation}>
            <CopyIcon />
            {copied ? text("source.copied") : text("source.citation")}
          </button>
        </div>
      </section>
      {sourceBusy && <p className="source-loading">{text("reader.sourceLoading")}</p>}
    </aside>
  );
}

function SharedSourceEvidence({ segment }) {
  const { text } = useLocale();
  const actions = segment?.source_actions || {};
  const publisher = actions.publisher_page_url;
  const readingCopy = actions.reading_copy?.view_url;
  const download = actions.reading_copy?.download_url;
  const attachments = (segment?.attachments || []).filter((item) =>
    isSafeHref(item?.url),
  );
  if (!isSafeHref(publisher) && !isSafeHref(readingCopy, { internal: true }) &&
      !isSafeHref(download, { internal: true }) && attachments.length === 0) return null;
  return (
    <details className="shared-source-disclosure">
    <summary>{text("source.heading")}</summary>
    <nav className="shared-source-evidence" aria-label={text("source.heading")}>
      {isSafeHref(publisher) && <a href={publisher} target="_blank" rel="noreferrer">{text("source.publisherLink")}</a>}
      {isSafeHref(readingCopy, { internal: true }) && <a href={readingCopy} target="_blank" rel="noreferrer">{text("source.readingCopy")}</a>}
      {isSafeHref(download, { internal: true }) && <a href={download} download>{text("source.download")}</a>}
      {attachments.map((item, index) => (
        <a key={item.attachment_id || item.id || item.url} href={item.url} target="_blank" rel="noreferrer">
          {item.title || item.name || `${text("source.attachment")} ${index + 1}`}
        </a>
      ))}
    </nav>
    </details>
  );
}

export default function DocumentReader({
  bundle,
  busy,
  onBack,
  originMode = "search",
  mobileReader,
  expressions,
  onCitationOpen,
  onCompareCurrent,
  onSourceOpen,
  query,
  viewMode,
  parallelBundles,
  onViewModeChange,
  focusMode,
  onFocusModeChange,
  sourcePreview,
  sourceBusy,
  onSourceClose,
  targetSequence,
}) {
  const { locale, text, count } = useLocale();
  const [activeSequence, setActiveSequence] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showAllCitations, setShowAllCitations] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [continuousMode, setContinuousMode] = useState(true);
  const [fontScale, setFontScale] = useState(1);
  const [openSequences, setOpenSequences] = useState(new Set());
  const [parallelSide, setParallelSide] = useState(() => {
    const side = readReaderUrlState().parallelSide;
    return side === "th" || side === "en" ? side : locale;
  });
  const scrollRef = useRef(null);
  const pendingReadingPosition = useRef(null);
  const pendingInitialScroll = useRef(null);
  const segments = useMemo(
    () => (bundle?.segments || []).filter(hasDisplayText),
    [bundle],
  );
  const outline = useMemo(
    () =>
      segments.filter(
        (segment) =>
          segment.display_heading ||
          segment.heading ||
          SECTION_LABEL_KEYS[segment.type],
      ),
    [segments],
  );
  const contextLabels = useMemo(
    () => ["applicable_law", "question", "holding"]
      .filter((kind) => segments.some((segment) => segment.type === kind)),
    [segments],
  );
  const relevantSegment = useMemo(
    () => findRelevantSegment(segments, query || ""),
    [query, segments],
  );
  const englishSegments =
    viewMode === "parallel" ? parallelBundles?.en?.segments || [] : [];
  const englishAlignment = parallelBundles?.en?.translation_metadata?.segment_alignment;
  const englishByStableId = useMemo(
    () => englishAlignment === "document_level_only"
      ? new Map()
      : uniqueStableAlignmentMap(englishSegments),
    [englishAlignment, englishSegments],
  );

  function changeParallelSide(side) {
    if (side === parallelSide) return;
    const content = scrollRef.current;
    if (content) {
      const scroll = getComputedStyle(content).overflowY === "visible" ? document.scrollingElement : content;
      const top = scroll === document.scrollingElement
        ? Math.max(0, document.querySelector(".reader-toolbar").getBoundingClientRect().bottom)
        : scroll.getBoundingClientRect().top;
      const section = [...content.querySelectorAll("details[data-sequence]")]
        .find((node) => node.getBoundingClientRect().bottom > top);
      if (section) {
        const rect = section.getBoundingClientRect();
        pendingReadingPosition.current = { scroll, section, fraction: (top - rect.top) / rect.height };
      }
    }
    writeReaderUrlState({ parallelSide: side });
    setParallelSide(side);
  }

  useLayoutEffect(() => {
    const position = pendingReadingPosition.current;
    if (position && position.section.isConnected) {
      const { scroll } = position;
      const top = scroll === document.scrollingElement
        ? Math.max(0, document.querySelector(".reader-toolbar").getBoundingClientRect().bottom)
        : scroll.getBoundingClientRect().top;
      const rect = position.section.getBoundingClientRect();
      scroll.scrollTo({ top: scroll.scrollTop + rect.top - top + position.fraction * rect.height, behavior: "instant" });
    }
    pendingReadingPosition.current = null;
  }, [parallelSide]);

  useLayoutEffect(() => {
    if (busy || !bundle || segments.length === 0) return;
    const provisionId = readReaderUrlState().provisionId;
    const target = segments.find((segment) => segment.provision_id === provisionId)
      || segments.find((segment) => Number(segment.sequence) === Number(targetSequence))
      || relevantSegment;
    setActiveSequence(target?.sequence ?? segments[0]?.sequence);
    setOpenSequences(
      new Set(
        continuousMode
          ? segments.map((segment) => segment.sequence)
          : [target?.sequence ?? segments[0]?.sequence],
      ),
    );
    pendingInitialScroll.current = target?.sequence ?? segments[0]?.sequence;
  }, [busy, bundle, continuousMode, relevantSegment, segments, targetSequence]);

  useLayoutEffect(() => {
    const sequence = pendingInitialScroll.current;
    if (busy || sequence == null || !openSequences.has(sequence)) return;
    const target = document.getElementById(`document-section-${sequence}`);
    if (!target) return;
    target.scrollIntoView({ behavior: "instant", block: "start" });
    pendingInitialScroll.current = null;
  }, [busy, bundle, openSequences]);

  useEffect(() => {
    if (busy || !bundle || !segments.length || typeof IntersectionObserver === "undefined") return undefined;
    const container = document.querySelector(".document-scroll");
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];
        const sequence = visible?.target?.dataset?.sequence;
        if (sequence != null) setActiveSequence(Number.isNaN(Number(sequence)) ? sequence : Number(sequence));
      },
      { root: container, rootMargin: "-12% 0px -65% 0px", threshold: [0, 0.25, 0.6] },
    );
    segments.forEach((segment) => {
      const node = document.getElementById(`document-section-${segment.sequence}`);
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, [busy, bundle, segments]);

  useEffect(() => {
    if (sourceBusy || sourcePreview) setEvidenceOpen(true);
  }, [sourceBusy, sourcePreview]);

  if (busy) {
    return <main className="reader loading">{text("reader.loading")}</main>;
  }
  if (!bundle) {
    return (
      <main className="reader reader-empty">
        <p>{text("reader.empty")}</p>
      </main>
    );
  }

  const {
    document: legalDocument,
    expression,
    source_item: source,
  } = bundle;
  const title = resolveDocumentTitle(legalDocument, locale);
  const englishTitleLine = chooseEnglishTitleLine({
    document: legalDocument,
    expressionLanguage: expression.language,
    title,
    parallelDocument: viewMode === "parallel" ? parallelBundles?.en?.document ?? null : null,
  });
  const citationTitle = expression.language === "en"
    ? legalDocument.title_en_official || legalDocument.title_en_translated || legalDocument.display_title_th || legalDocument.title_th
    : legalDocument.display_title_th || legalDocument.title_th;
  const thaiOriginalOnly = locale === "en" &&
    expression.language !== "en" && !expressions.some((item) => item.language === "en");
  const citation =
    expression.language === "en"
      ? legalDocument.official_citation_en || citationTitle
      : legalDocument.official_citation_th || citationTitle;
  const translationEvidence = getTranslationEvidence(bundle, locale);
  const parallelTranslationEvidence = parallelBundles?.en
    ? getTranslationEvidence(parallelBundles.en, locale)
    : null;
  const translationNotice = parallelTranslationEvidence || translationEvidence;
  const showTranslationStatus = (
    translationNotice.status === "machine_draft" ||
    translationNotice.status === "lawyer_reviewed" ||
    (translationNotice.status === "official" && translationNotice.tone === "confirmed")
  );
  const documentTypeLabel = typeLabel(legalDocument.document_type_code, locale);
  const sourceLink = publisherUrl(source, legalDocument.canonical_source_url);
  // This panel follows the provision being read. A document-wide list made
  // headings and footnotes from unrelated sections look like citations of the
  // active provision and grew into hundreds of distracting rows.
  const resolvedCitations = citationsForSegment(
    bundle.citations,
    activeSequence,
  );

  function scrollToSegment(sequence) {
    const target = segments.find((segment) => segment.sequence === sequence);
    writeReaderUrlState({ provisionId: target?.provision_id || "" });
    setActiveSequence(sequence);
    setOpenSequences((current) => continuousMode ? new Set([...current, sequence]) : new Set([sequence]));
    document
      .getElementById(`document-section-${sequence}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function copyCitation() {
    await navigator.clipboard.writeText(
      [citation, sourceLink].filter(Boolean).join(" — "),
    );
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <main className={`reader ${mobileReader ? "mobile-reader-open" : ""}`}>
      <button className="mobile-back" type="button" onClick={onBack}>
        <ArrowIcon />
        {originMode === "browse" ? text("reader.backBrowse") : text("reader.backSearch")}
      </button>
      <article>
        <header className="document-header">
          <div className="document-breadcrumb">
            <span>{documentTypeLabel}</span>
            {legalDocument.official_number && (
              <>
                <span aria-hidden="true">/</span>
                <span>{legalDocument.official_number}</span>
              </>
            )}
          </div>
          <div className="reader-toolbar document-topline" role="toolbar" aria-label={text("reader.toolbar")}>
            {viewMode === "parallel" && (
              <div className="parallel-mobile-switch" role="group" aria-label={text("reader.parallelSide")}>
                {['th', 'en'].map((side) => (
                  <button key={side} type="button" aria-pressed={parallelSide === side}
                    onClick={() => changeParallelSide(side)}>{side.toUpperCase()}</button>
                ))}
              </div>
            )}
            <div className="language-switch" aria-label={text("header.documentLanguage")}>
              <button
                type="button"
                className={viewMode === "th" ? "active" : ""}
                disabled={!expressions.some((item) => item.language === "th")}
                onClick={() => onViewModeChange("th")}
              >
                {text("reader.thai")}
              </button>
              <button
                type="button"
                className={viewMode === "parallel" ? "active" : ""}
                disabled={
                  !expressions.some((item) => item.language === "th") ||
                  !expressions.some((item) => item.language === "en")
                }
                onClick={() => onViewModeChange("parallel")}
              >
                {text("reader.parallel")}
              </button>
              <button
                type="button"
                className={viewMode === "en" ? "active" : ""}
                disabled={!expressions.some((item) => item.language === "en")}
                onClick={() => onViewModeChange("en")}
              >
                {text("reader.english")}
              </button>
              {(expression.language === "en" || viewMode === "parallel") && showTranslationStatus && (
                <p className={`translation-disclosure translation-status-${translationNotice.tone || "source"}`}>
                  <strong>{translationNotice.badge}</strong>
                </p>
              )}
            </div>
            <div className="document-actions">
              <div className="font-controls" aria-label={text("reader.fontSize")}>
                <button
                  type="button"
                  aria-label={text("reader.decreaseFont")}
                  onClick={() =>
                    setFontScale((current) => Math.max(0.9, current - 0.1))
                  }
                >
                  A−
                </button>
                <button
                  type="button"
                  aria-label={text("reader.resetFont")}
                  onClick={() => setFontScale(1)}
                >
                  A
                </button>
                <button
                  type="button"
                  aria-label={text("reader.increaseFont")}
                  onClick={() =>
                    setFontScale((current) => Math.min(1.3, current + 0.1))
                  }
                >
                  A+
                </button>
              </div>
              <button
                type="button"
                className={`secondary-button ${focusMode ? "active" : ""}`}
                onClick={() => onFocusModeChange(!focusMode)}
              >
                {focusMode ? text("reader.focusOn") : text("reader.focusOff")}
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setOutlineOpen((current) => !current)}
              >
                {outlineOpen ? text("reader.hideOutline") : text("reader.showOutline")}
              </button>
              <button
                type="button"
                className={`secondary-button ${continuousMode ? "active" : ""}`}
                onClick={() => setContinuousMode((current) => !current)}
              >
                {continuousMode ? text("reader.continuousOn") : text("reader.continuousOff")}
              </button>
              <button type="button" className="secondary-button compare-button" onClick={onCompareCurrent}>
                {text("reader.addCompare")}
              </button>
              {resolvedCitations.length > 0 && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() =>
                    document
                      .getElementById(`citations-${activeSequence}`)
                      ?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                >
                  {text("reader.citedDocuments")}
                </button>
              )}
              <button
                type="button"
                className={`source-button ${evidenceOpen ? "active" : ""}`}
                aria-expanded={evidenceOpen}
                onClick={() => setEvidenceOpen((current) => !current)}
              >
                <SourceIcon />
                {text("reader.sourceAndRelated")}
              </button>
            </div>
          </div>
          <h1 lang={title.language}>{title.text}</h1>
          {title.isFallback && title.text && !thaiOriginalOnly && (
            <span className="title-language-note" lang={locale}>
              {text(title.language === "th" ? "reader.titleInThai" : "reader.titleInEnglish")}
            </span>
          )}
          {thaiOriginalOnly && (
            <span className="thai-original-only" lang="en">{text("reader.thaiOriginalOnly")}</span>
          )}
          {englishTitleLine?.kind === "parallel" && (
              <div className="parallel-heading-status">
                <p className="parallel-title" lang={englishTitleLine.language}>{englishTitleLine.text}</p>
                {englishTitleLine.isFallback && (
                  <small lang={locale}>{text(englishTitleLine.language === "th" ? "reader.titleInThai" : "reader.titleInEnglish")}</small>
                )}
              </div>
          )}
          {englishTitleLine?.kind === "translation" && (
              <div className="title-translation">
                <p lang="en">{englishTitleLine.text}</p>
              </div>
            )}
          {viewMode === "parallel" &&
            englishAlignment === "document_level_only" && (
              <p className="translation-disclosure">{text("reader.parallelDocumentOnly")} {text("reader.chooseEnglish")}</p>
          )}
          <LegalTimeline document={legalDocument} />
        </header>

        <div className={`reader-body ${outlineOpen ? "" : "outline-collapsed"} ${evidenceOpen ? "" : "evidence-collapsed"}`}>
          <details
            className="document-outline-disclosure"
            open={outlineOpen}
            onToggle={(event) => setOutlineOpen(event.currentTarget.open)}
          >
            <summary>{text("reader.structureAndContext")}</summary>
            <nav className="document-outline" aria-label={text("reader.outline")}>
              <div>
                <span className="eyebrow">{text("reader.outlineTitle")}</span>
                <strong>{documentTypeLabel}</strong>
              </div>
              {contextLabels.length > 0 && (
                <div className="reader-context-labels" aria-label={text("reader.structureAndContext")}>
                  {contextLabels.map((kind) => <span key={kind}>{text(`reader.${kind === "applicable_law" ? "applicableLaw" : kind}`)}</span>)}
                </div>
              )}
              <ul>
                {outline.map((segment) => (
                  <li key={segment.sequence}>
                    <button
                      className={
                        activeSequence === segment.sequence ? "active" : ""
                      }
                      lang={sectionPresentation(segment, {
                        locale,
                        language: expression.language,
                        pairedSegment: englishByStableId.get(stableAlignmentKey(segment)),
                      }).language}
                      onClick={() => scrollToSegment(segment.sequence)}
                    >
                      {sectionPresentation(segment, {
                        locale,
                        language: expression.language,
                        pairedSegment: englishByStableId.get(stableAlignmentKey(segment)),
                      }).text}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </details>

          <div className="document-scroll" ref={scrollRef}>
            <div
              className={`document-content ${viewMode === "parallel" ? "parallel-content" : ""}`}
              style={{ "--reader-font-scale": fontScale }}
              data-parallel-side={parallelSide}
            >
              {segments.length === 0 && (
                <p className="reader-text-unavailable" lang={locale}>{text("reader.textUnavailable")}</p>
              )}
              {segments.map((segment, index) => {
                const english = englishByStableId.get(stableAlignmentKey(segment));
                const segmentCitations = citationsForSegment(
                  bundle.citations,
                  segment.sequence,
                );
                const visibleSegmentCitations = showAllCitations
                  ? segmentCitations
                  : segmentCitations.slice(0, 12);
                return (
                  <details
                    id={`document-section-${segment.sequence}`}
                    data-sequence={segment.sequence}
                    key={`${segment.type}-${segment.sequence}`}
                    className={
                      activeSequence === segment.sequence ? "active-section" : ""
                    }
                    open={openSequences.has(segment.sequence)}
                  >
                  <summary
                    onClick={(event) => {
                      event.preventDefault();
                      writeReaderUrlState({ provisionId: segment.provision_id || "" });
                      setActiveSequence(segment.sequence);
                      setOpenSequences((current) => {
                        if (!continuousMode) return new Set([segment.sequence]);
                        const next = new Set(current);
                        if (next.has(segment.sequence)) next.delete(segment.sequence);
                        else next.add(segment.sequence);
                        return next;
                      });
                    }}
                  >
                    {viewMode === "parallel" ? (
                      <div className="parallel-section-headings">
                        <h2 className="parallel-th" lang="th">
                          {sectionPresentation(segment, { locale: "th", language: "th" }).text}
                        </h2>
                        {english ? (
                          <h2 className="parallel-en" lang="en">
                            {sectionPresentation(english, { locale: "en", language: "en" }).text}
                          </h2>
                        ) : (
                          <span className="parallel-section-heading-empty" aria-hidden="true" />
                        )}
                      </div>
                    ) : (
                      <h2 lang={sectionPresentation(segment, {
                        locale,
                        language: expression.language,
                        pairedSegment: englishByStableId.get(stableAlignmentKey(segment)),
                      }).language}>
                        {sectionPresentation(segment, {
                          locale,
                          language: expression.language,
                          pairedSegment: englishByStableId.get(stableAlignmentKey(segment)),
                        }).text}
                      </h2>
                    )}
                  </summary>
                  {openSequences.has(segment.sequence) && (
                  <div
                    className={
                      viewMode === "parallel"
                        ? "parallel-reading"
                        : "single-reading"
                    }
                  >
                    <div className={viewMode === "parallel" ? "parallel-th" : undefined}>
                      {viewMode === "parallel" && (
                        <span className="reading-language">{text("reader.thai")}</span>
                      )}
                      <LegalText
                        segment={segment}
                        citations={bundle.citations}
                        language={bundle.expression.language}
                        onCitationOpen={onCitationOpen}
                        onSourceOpen={onSourceOpen}
                        source={source}
                        hideNotes={viewMode === "parallel"}
                      />
                        {viewMode === "parallel" && <SharedSourceEvidence segment={segment} />}
                    </div>
                    {viewMode === "parallel" && (
                      <div className="parallel-en" lang="en">
                        <span className="reading-language">{text("reader.english")}</span>
                        {english ? (
                          <LegalText
                            segment={english}
                            citations={parallelBundles?.en?.citations}
                            language="en"
                            onCitationOpen={onCitationOpen}
                            onSourceOpen={onSourceOpen}
                            source={parallelBundles?.en?.source_item || source}
                            hideNotes
                          />
                        ) : (
                          <div className="translation-missing">
                            <strong>{text("reader.translationNotPaired")}</strong>
                            <p>{text("reader.translationMissingDetail")}</p>
                          </div>
                        )}
                        {english && <SharedSourceEvidence segment={segment} />}
                      </div>
                    )}
                    {viewMode === "parallel" && (
                      <div className="parallel-shared-notes">
                        <div className="parallel-th">
                        <LegalText segment={segment} citations={bundle.citations} notesOnly language={bundle.expression.language}
                          onCitationOpen={onCitationOpen} onSourceOpen={onSourceOpen} source={source} />
                        </div>
                        <div className="parallel-en">
                        {english && <LegalText segment={english} citations={parallelBundles?.en?.citations} notesOnly language="en"
                          onCitationOpen={onCitationOpen} onSourceOpen={onSourceOpen} source={parallelBundles?.en?.source_item || source} />}
                        </div>
                      </div>
                    )}
                    {segmentCitations.length > 0 && (
                      <details
                        className="citation-panel"
                        id={`citations-${segment.sequence}`}
                      >
                        <summary>
                          <span>{text("reader.citedDocuments")}</span>
                        </summary>
                        <div className="citation-panel-body">
                          <p>{text("reader.citePanelHint")}</p>
                          <ul>
                            {visibleSegmentCitations.map((item) => (
                              <li key={item.resolved_target_id}>
                                <div><strong>{item.raw_text}</strong></div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    onCitationOpen(
                                      item.resolved_target_expression_id,
                                      item.resolved_target_work_id,
                                      item.raw_text,
                                    )
                                  }
                                >
                                  {text("reader.compareCited")}
                                </button>
                              </li>
                            ))}
                          </ul>
                          {segmentCitations.length > visibleSegmentCitations.length && (
                            <button
                              type="button"
                              className="show-more-citations"
                              onClick={() => setShowAllCitations(true)}
                            >
                              {text("reader.moreCitations", {
                                count: count(
                                  segmentCitations.length -
                                    visibleSegmentCitations.length,
                                ),
                              })}
                            </button>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                  )}
                </details>
                );
              })}
            </div>
          </div>
          {evidenceOpen && (
            <EvidenceRail
              source={source}
              sourcePreview={sourcePreview}
              sourceBusy={sourceBusy}
              onCopyCitation={copyCitation}
              copied={copied}
              onClose={() => {
                onSourceClose();
                setEvidenceOpen(false);
              }}
            />
          )}
        </div>
      </article>
    </main>
  );
}
